/**
 * Mixpanel 화면 추적 매니저 (NZSC-3077 직거래 1차 사이클).
 *
 * 데이터 모델: 이벤트 3개 (view_page / click_action / exit_screen)
 *              + 속성 7개 (screen_name, element_type, action_target,
 *              time_on_screen_sec, scroll_max_depth_pct,
 *              current_screen_name [super property], route_path [super property]).
 *
 * 책임:
 *   1) trackView(screen_name, element_type): 이전 화면 강제 exit + super
 *      property 'current_screen_name' 갱신 + view_page 발사 + 타이머/스크롤 시작.
 *   2) trackClick(action_target): click_action 발사. current_screen_name은
 *      super property로 자동 첨부.
 *   3) trackExit(): exit_screen 발사 (time_on_screen_sec / scroll_max_depth_pct
 *      포함). visibility hidden 동안 누적 일시정지.
 *
 * exit 트리거:
 *   - VueRouter beforeEach (router/index.js) → 라우트 변경 시
 *   - window beforeunload / pagehide
 *   - 컴포넌트 watcher (팝업 닫힘 / 상태 분기 전환 시)
 *
 * 페어링: trackView 호출 시 currentScreen이 있으면 강제 exit 발화 → 중복 방지.
 */

class MixpanelTracker {
  constructor(mixpanel) {
    this.mp = mixpanel;
    /**
     * { screen_name, element_type, mountedAt (ms), totalActiveMs,
     *   scrollMaxPct, lastVisibleAt (ms or null) }
     * null = 추적 중인 화면 없음 (idle).
     */
    this.current = null;
    this._installVisibilityListener();
    this._installScrollListener();
    this._installUnloadListener();
  }

  trackView(screen_name, element_type, properties = {}) {
    if (!this.mp || typeof this.mp.track !== "function") return;
    if (!screen_name) return;
    // 페어링 보장 — 이전 화면이 trackExit 못 한 채 새 view가 오면 강제 exit
    if (this.current) this.trackExit();
    try {
      this.mp.register({ current_screen_name: screen_name });
    } catch (e) {
      // register 실패해도 track은 진행
    }
    this.mp.track("view_page", {
      ...properties,
      screen_name: screen_name,
      element_type: element_type || "page",
    });
    var nowVisible = typeof document !== "undefined" && document.visibilityState === "visible";
    this.current = {
      screen_name: screen_name,
      element_type: element_type || "page",
      mountedAt: Date.now(),
      totalActiveMs: 0,
      scrollMaxPct: 0,
      lastVisibleAt: nowVisible ? Date.now() : null,
    };
  }

  trackClick(action_target, properties = {}) {
    if (!this.mp || typeof this.mp.track !== "function") return;
    if (!action_target) return;
    this.mp.track("click_action", { ...properties, action_target: action_target });
  }

  trackExit() {
    if (!this.mp || typeof this.mp.track !== "function") return;
    if (!this.current) return;
    // visibility 누적 정산
    if (this.current.lastVisibleAt) {
      this.current.totalActiveMs += Date.now() - this.current.lastVisibleAt;
      this.current.lastVisibleAt = null;
    }
    var time_on_screen_sec = Math.max(0, Math.round(this.current.totalActiveMs / 1000));
    var exitProps = {
      screen_name: this.current.screen_name,
      element_type: this.current.element_type,
      time_on_screen_sec: time_on_screen_sec,
      scroll_max_depth_pct: this.current.scrollMaxPct,
    };
    // 자체수집 미러 — exit는 래퍼($track_exit) 밖(view 페어링·pagehide·라우터)에서도
    // 발화되므로 여기(발사 지점)에 미러를 둔다. main.js가 mirror 콜백 주입.
    // mp.track보다 먼저: Mixpanel SDK가 props 객체에 token 등을 mutate해 묻히는 것 방지.
    if (typeof this.mirror === "function") {
      try {
        this.mirror("exit_screen", exitProps);
      } catch (e) {
        // 미러 실패가 트래킹/서비스에 영향 주지 않음
      }
    }
    this.mp.track("exit_screen", exitProps);
    this.current = null;
  }

  _installVisibilityListener() {
    if (typeof document === "undefined" || !document.addEventListener) return;
    var self = this;
    document.addEventListener("visibilitychange", function () {
      if (!self.current) return;
      if (document.visibilityState === "hidden") {
        // 백그라운드 진입 — 타이머 일시정지 (단순 탭 전환에서는 exit 발화 안 함)
        if (self.current.lastVisibleAt) {
          self.current.totalActiveMs += Date.now() - self.current.lastVisibleAt;
          self.current.lastVisibleAt = null;
        }
      } else if (document.visibilityState === "visible") {
        // 포그라운드 복귀 — 타이머 재개
        self.current.lastVisibleAt = Date.now();
      }
    });
  }

  _installScrollListener() {
    if (typeof window === "undefined") return;
    var self = this;
    var last = 0;
    var handler = function () {
      var now = Date.now();
      if (now - last < 200) return; // throttle 200ms
      last = now;
      if (!self.current) return;
      var doc = document.documentElement || {};
      var scrollY = window.scrollY || doc.scrollTop || 0;
      var vh = window.innerHeight || doc.clientHeight || 0;
      var sh = doc.scrollHeight || 1;
      var pct = Math.round(((scrollY + vh) / sh) * 100);
      if (pct < 0) pct = 0;
      if (pct > 100) pct = 100;
      if (pct > self.current.scrollMaxPct) self.current.scrollMaxPct = pct;
    };
    window.addEventListener("scroll", handler, { passive: true });
  }

  _installUnloadListener() {
    if (typeof window === "undefined") return;
    var self = this;
    // beforeunload / pagehide: 페이지 떠날 때 exit 보장 (Mixpanel 큐가 비동기지만
    // batch flush까지 100ms 정도 잡힘 — 완벽 보장 X, best effort)
    var flush = function () {
      try {
        self.trackExit();
      } catch (e) {
        // ignore
      }
    };
    window.addEventListener("beforeunload", flush);
    window.addEventListener("pagehide", flush);
  }
}

let _tracker = null;

export function initTracker(mixpanel) {
  _tracker = new MixpanelTracker(mixpanel);
  return _tracker;
}

export function getTracker() {
  return _tracker;
}

export default { initTracker: initTracker, getTracker: getTracker };
